import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { Observable, from } from "rxjs";
import { clearAllCookies } from "./cookies";
import { cacheManager } from "../infrastructure/cache-manager";

// ---------------------------------------------------------------------------
// Cookie helpers
// ---------------------------------------------------------------------------

export function getCookie(cname: string): string {
  const name = cname + "=";
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
}

export function setCookie(cname: string, cvalue: string, exdays: number): void {
  const d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  const expires = "expires=" + d.toUTCString();
  const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const secureFlag = isHttps ? ';Secure' : '';
  document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/" + secureFlag + ";SameSite=Lax";
}


// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type QueueEntry = {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
};

// ---------------------------------------------------------------------------
// HttpClient
// ---------------------------------------------------------------------------

class HttpClient {
  private client: AxiosInstance;

  /**
   * Guards against concurrent refresh calls.
   * While true, every 401 response is queued instead of triggering a new refresh.
   */
  private isRefreshing = false;

  /**
   * Queue of pending requests that arrived while a refresh was in progress.
   * Each entry is resolved/rejected once the refresh settles.
   */
  private failedQueue: QueueEntry[] = [];

  constructor(uid: string, cid: string | null) {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_URL_POS,
      headers: {
        "Access-Control-Allow-Origin": "*",
        "x-uid": uid,
        "x-cid": cid || localStorage.getItem('cid'),
        Authorization: `Bearer ${getCookie("token")}`,
      }
    });

    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }

  // -------------------------------------------------------------------------
  // Interceptor setup
  // -------------------------------------------------------------------------

  private setupRequestInterceptor(): void {
    this.client.interceptors.request.use((config) => {
      const impersonateToken = getCookie("owner_user_id");
      if (impersonateToken) {
        config.headers["x-impersonate-token"] = impersonateToken;
      }
      return config;
    });
  }

  private setupResponseInterceptor(): void {
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest: AxiosRequestConfig & { _retry?: boolean, _isNetworkErrorFallback?: boolean } =
          error.config;

        // --- 401: Token inválido o expirado (backend: "Token inválido o expirado") ---
        if (error?.response?.status === 401 && !originalRequest._retry) {
          const refreshToken = getCookie("refreshToken");

          if (!refreshToken) {
            // No hay refresh token — cierre de sesión inmediato
            this.dispatchAuthFailure();
            return Promise.reject(error);
          }

          if (this.isRefreshing) {
            // Hay un refresh en curso: encolar esta petición como Observador
            return new Promise<string>((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then((newToken) => {
              originalRequest.headers = {
                ...(originalRequest.headers ?? {}),
                Authorization: `Bearer ${newToken}`,
              };
              return this.client(originalRequest);
            });
          }

          // Primera petición en fallar: iniciar el refresco
          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const newToken = await this.requestToken(refreshToken);
            this.processQueue(newToken, null);
            originalRequest.headers = {
              ...(originalRequest.headers ?? {}),
              Authorization: `Bearer ${newToken}`,
            };
            return this.client(originalRequest);
          } catch (refreshError) {
            this.processQueue(null, refreshError);
            this.dispatchAuthFailure();
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        // --- 403: Permiso denegado (impersonation / rol) — no reintentar ---
        if (error?.response?.status === 403) {
          return Promise.resolve({ data: null });
        }

        // --- 410: Configuración de acceso inválida — limpiar sesión completa ---
        if (error?.response?.status === 410) {
          this.clearSession();
          return Promise.reject("Session expired. Redirecting to login.");
        }

        return Promise.reject(error);
      }
    );
  }

  // -------------------------------------------------------------------------
  // Queue helpers (Patrón Observer)
  // -------------------------------------------------------------------------

  /**
   * Notifica a todas las peticiones encoladas con el resultado del refresh.
   * @param token   Nuevo token si el refresh fue exitoso.
   * @param error   Error si el refresh falló.
   */
  private processQueue(token: string | null, error: unknown): void {
    this.failedQueue.forEach((entry) => {
      if (error) {
        entry.reject(error);
      } else {
        entry.resolve(token as string);
      }
    });
    this.failedQueue = [];
  }

  // -------------------------------------------------------------------------
  // Token refresh
  // -------------------------------------------------------------------------

  /**
   * Llama al endpoint de refresh y actualiza las cookies y cabeceras.
   * Devuelve el nuevo id_token.
   */
  private async requestToken(refreshToken: string): Promise<string> {
    const response = await axios.post(
      process.env.REACT_APP_URL_POS + "/auth/refresh",
      {
        grant_type: "refresh_token",
        refresh_token: refreshToken,
      },
      {
        headers: {
          "Access-Control-Allow-Origin": "*",
          "x-uid": this.client.defaults.headers?.["x-uid"],
          "x-cid": this.client.defaults.headers?.["x-cid"],
          Authorization: `Bearer ${getCookie("token")}`,
        },
      }
    );

    const newToken: string = response.data.data.id_token;
    const now = new Date().getTime().toString();

    setCookie("token", newToken, 1);
    setCookie("tokenTimeStamp", now, 1);

    // Actualizar cabecera por defecto para peticiones futuras
    this.client.defaults.headers["Authorization"] = `Bearer ${newToken}`;

    return newToken;
  }

  // -------------------------------------------------------------------------
  // Session helpers
  // -------------------------------------------------------------------------

  private dispatchAuthFailure(): void {
    window.dispatchEvent(new Event("auth-failure"));
  }

  private clearSession(): void {
    if (typeof window !== "undefined") {
      window.localStorage.clear();
      window.sessionStorage.clear();
    }
    clearAllCookies();
    cacheManager.clear();
    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
  }


  // -------------------------------------------------------------------------
  // Public HTTP methods
  // -------------------------------------------------------------------------

  get<T>(url: string, params: Record<string, unknown> = {}): Observable<T> {
    return from(
      this.client.get<T>(url, { params }).then((res) => res?.data)
    );
  }

  post<T>(url: string, data: unknown): Observable<T> {
    return from(this.client.post<T>(url, data).then((res) => res?.data));
  }

  put<T>(url: string, data: unknown): Observable<T> {
    return from(this.client.put<T>(url, data).then((res) => res?.data));
  }

  delete<T>(url: string): Observable<T> {
    return from(this.client.delete<T>(url).then((res) => res?.data));
  }

  patch<T>(url: string, data: unknown): Observable<T> {
    return from(this.client.patch<T>(url, data).then((res) => res?.data));
  }

  /** Peticiones que no necesitan refresco de token (e.g. login). */
  get_no_refresh<T>(url: string): Observable<T> {
    return from(this.client.get<T>(url).then((res) => res?.data));
  }

  post_no_refresh<T>(url: string, data: unknown): Observable<T> {
    return from(this.client.post<T>(url, data).then((res) => res?.data));
  }
}

export default HttpClient;
